/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import _ from 'lodash';
import Types from '../constants/actionTypes';

const defaultTeam = {
  short_name: '',
  description: '',
  image: {},
  members: [],
  deleted_member_ids: [],
  kanban: false
};

const initialState = {
  isFetching: false,
  isSending: false,
  roleChanging: false,
  teams: [],
  art: [],
  solutionTrains: [],
  team: defaultTeam,
  metrics: {}
};

const replace = (teams, team = {}) =>
  teams.map((oldTeam) =>
    oldTeam.id === team.id ? { ...team } : { ...oldTeam }
  );

const deleteTeam = (teams, team) => teams.filter((t) => t.id !== team.id);

const addMember = (teams, teamId, user) => {
  const team = teams.find((t) => t.id === teamId);
  team.members.push(user);
  return teams.map((t) => {
    if (t.id === team.id) {
      return team;
    }
    return t;
  });
};

const teamReducer = (state = initialState, action) => {
  const { teams, team, user, teamId, product, productId, users } = action;
  const { teams: teamsState } = state;
  const { metrics } = state;
  let updatedTeams;
  let updatedTeam;
  switch (action.type) {
    case Types.REQUEST_TEAM_UPDATE:
    case Types.REQUEST_TEAM_CREATE:
    case Types.REQUEST_TEAMS:
      return { ...state, isSending: true };
    case Types.REQUEST_TEAMS_FAILED:
      return { ...state, isSending: false, isFetching: false };
    case Types.RECEIVE_TEAMS:
      return {
        ...state,
        isFetching: false,
        isSending: false,
        teams: teams.filter((element) => element.level === 1),
        art: teams.filter((element) => element.level === 2),
        solutionTrains: teams.filter((element) => element.level === 3)
      };
    case Types.RECEIVE_USER:
      // when teams are retrieved ahead of users, match the team member ids to the users
      // eslint-disable-next-line no-case-declarations
      const newTeams = state.teams.map((t) => {
        const memberIds =
          t.member_ids?.length > 0 ? t.member_ids : t.members.map((m) => m.id); // depends on which serializer used for the team
        const deletedMemberIds =
          t.deleted_member_ids?.length > 0
            ? t.deleted_member_ids
            : t.deleted_members.map((m) => m.id); // depends on which serializer used for the team
        return {
          ...t,
          members: users.filter((u) => memberIds.some((uid) => uid === u.id)),
          deleted_members: users.filter((u) =>
            deletedMemberIds.some((uid) => uid === u.id)
          )
        };
      });
      return {
        ...state,
        teams: newTeams
      };
    case Types.RECEIVE_ORGANIZATION_USER:
      // when a user is added to an organization with team, make sure the team shows the user as a member
      // eslint-disable-next-line no-case-declaration, no-case-declarations
      const newOrgTeams = state.teams.map((t) => {
        let { members } = t;
        if (user.teams.includes(t) && !t.members.include(user))
          members = { ...t.members, user };
        return {
          ...t,
          members
        };
      });
      return {
        ...state,
        teams: newOrgTeams
      };

    case Types.ADD_TEAM:
      // eslint-disable-next-line no-case-declarations
      const teamPosition = teamsState.findIndex((t) => t.id === team.id);
      updatedTeams =
        teamPosition === -1
          ? [...teamsState, team]
          : [
              ...teamsState.slice(0, teamPosition),
              team,
              ...teamsState.slice(teamPosition + 1)
            ];

      return {
        ...state,
        isFetching: false,
        isSending: false,
        teams: updatedTeams
      };
    case Types.UPDATE_TEAM:
      return {
        ...state,
        isFetching: false,
        isSending: false,
        teams: replace([...state.teams], team)
      };
    case Types.DELETE_TEAM:
      return {
        ...state,
        isFetching: false,
        teams: deleteTeam([...state.teams], team)
      };
    case Types.ADD_MEMBER:
      return {
        ...state,
        isFetching: false,
        teams: addMember(_.cloneDeep(state.teams), team.id, user)
      };
    case Types.SEND_UPDATE_TEAM:
      return { ...state, isSending: true };
    case Types.UPLOAD_TEAM_PICTURE:
      return { ...state, isFetching: true };
    case Types.ADD_NEW_TEAM:
      return { ...state, team, isSending: false };
    case Types.CLEAR_NEW_TEAM:
      return { ...state, team: { ...defaultTeam } };
    case Types.TEAM_ERROR:
    case Types.APPLICATION_ERROR:
      return { ...state, isFetching: false, isSending: false };
    case Types.ROLE_CHANGE:
      return { ...state, roleChanging: action.payload };
    case Types.REQUEST_TEAM_GRAPHS:
      return {
        ...state,
        metrics: {
          ...metrics,
          [action.teamId]: {
            ...metrics[action.teamId],
            [action.graph]: undefined
          }
        }
      };
    case Types.ADD_TEAM_METRICS_BURNDOWN:
      if (metrics[action.teamId]) {
        metrics[action.teamId].burndown = action.burndown;
      } else {
        metrics[action.teamId] = {
          burndown: action.burndown
        };
      }
      return {
        ...state,
        metrics: {
          ...metrics,
          [action.teamId]: { ...metrics[action.teamId] }
        }
      };
    case Types.ADD_TEAM_METRICS_CUMULATIVE:
      if (metrics[action.teamId]) {
        metrics[action.teamId].cumulative = action.cumulative;
      } else {
        metrics[action.teamId] = {
          cumulative: action.cumulative
        };
      }
      return {
        ...state,
        metrics: {
          ...metrics,
          [action.teamId]: { ...metrics[action.teamId] }
        }
      };
    case Types.UPDATE_TEAM_MEMBER:
      updatedTeams = teamsState.map((t) => {
        let { members, member_ids, deleted_members, deleted_member_ids, authorized_result_owner_ids } = t;
        if (members.some((member) => member.id === user.id)) {
          members = members.map((member) => {
            if (member.id === user.id) {
              return {
                ...member,
                ...user
              };
            }
            return member;
          });
          member_ids = members.map((m) => m.id);

          return {
            ...t,
            member_ids,
            members
          };
        }
        // if user has been added to the team but is not a team member, add as a team member
        // the alternative to this logic is re-fetching the team
        if (user.teams.some((ut) => ut.id === t.id)) {
          const dmIndex = deleted_members.findIndex((dm) => dm.id === user.id);
          if (dmIndex > -1) {
            // if was deleted previously
            deleted_members = [
              ...deleted_members.slice(0, dmIndex),
              ...deleted_members.slice(dmIndex + 1, deleted_members.length)
            ];
            deleted_member_ids = deleted_members.map((dm) => dm.id);
          }
          if (user.team_roles.some((tr) => tr.team_id === t.id && tr.type === "Worker"))
            authorized_result_owner_ids.push(user.id); //in for a penny, in for a pound -- ensure workers show up as owners
          members.push(user);
          member_ids = members.map((m) => m.id);
          return {
            ...t,
            authorized_result_owner_ids,
            deleted_member_ids,
            deleted_members,
            member_ids,
            members
          };
        }
        return t;
      });

      if (state.team.members.some((member) => member.id === action.user.id)) {
        updatedTeam = { ...state.team };
        updatedTeam.members = state.team.members.map((member) => {
          if (member.id === action.user.id) {
            return {
              ...member,
              ...action.user
            };
          }
          return member;
        });
      } else {
        updatedTeam = state.team;
      }

      return {
        ...state,
        team: updatedTeam,
        teams: updatedTeams
      };
    case Types.ADD_TEAM_METRICS_LEAN:
      if (metrics[action.teamId]) {
        metrics[action.teamId].lean = action.lean;
      } else {
        metrics[action.teamId] = {
          cycle: action.lean
        };
      }
      return {
        ...state,
        metrics: {
          ...metrics,
          [action.teamId]: { ...metrics[action.teamId] }
        }
      };
    case Types.ADD_TEAM_METRICS_VELOCITY:
      if (metrics[action.teamId]) {
        metrics[action.teamId].velocity = action.velocity;
      } else {
        metrics[action.teamId] = {
          velocity: action.velocity
        };
      }
      return {
        ...state,
        metrics: {
          ...metrics,
          [action.teamId]: { ...metrics[action.teamId] }
        }
      };

    case Types.ADD_PRODUCT_TO_TEAM:
      updatedTeams = teamsState.map((currentTeam) => {
        if (currentTeam.id === teamId) {
          return {
            ...currentTeam,
            products: [...currentTeam.products, product]
          };
        }
        return currentTeam;
      });
      return {
        ...state,
        teams: updatedTeams
      };

    case Types.REMOVE_PRODUCT_FROM_TEAM:
      updatedTeams = teamsState.map((currentTeam) => {
        if (currentTeam.id === teamId) {
          const newProducts = currentTeam.products.filter(
            (p) => p.id !== productId
          );
          return {
            ...currentTeam,
            products: newProducts
          };
        }
        return currentTeam;
      });

      return {
        ...state,
        teams: updatedTeams
      };
    case Types.CLEAR_ACTIVE_SPINNER:
      return { ...state, isFetching: false, isSending: false };
    default:
      return state;
  }
};

export default teamReducer;
